import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = '/api';

// Configure axios to send auth header
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasProcessedCallback = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = sessionStorage.getItem('session_token');
    if (!token) {
      setUser(null);
      return false;
    }
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      setError(null);
      return true;
    } catch (e) {
      sessionStorage.removeItem('session_token');
      setUser(null);
      return false;
    }
  }, []);

  // Combined effect for OAuth callback and initial auth check
  useEffect(() => {
    const init = async () => {
      // CRITICAL: If returning from OAuth callback, process session_id first
      // session_id comes in the URL fragment (hash), not query params
      const hash = window.location.hash;
      
      if (hash && hash.includes('session_id=') && !hasProcessedCallback.current) {
        hasProcessedCallback.current = true;
        
        const hashParams = new URLSearchParams(hash.substring(1));
        const sessionId = hashParams.get('session_id');
        
        if (sessionId) {
          try {
            const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
            
            if (res.data.session_token) {
              sessionStorage.setItem('session_token', res.data.session_token);
            }
            
            setUser(res.data.user);
            setError(null);
            // Clear hash and redirect to dashboard
            window.history.replaceState({}, document.title, '/dashboard');
            setLoading(false);
            return;
          } catch (e) {
            const detail = e.response?.data?.detail;
            const status = e.response?.status;
            let errorMsg = 'Error de autenticacion';
            if (detail) {
              errorMsg = detail;
            } else if (status === 0 || !e.response) {
              errorMsg = 'No se pudo conectar con el servidor. Verifica la URL del backend.';
            } else if (status === 500) {
              errorMsg = 'Error interno del servidor al verificar sesion';
            }
            setError(errorMsg);
            setUser(null);
            // Clear hash
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
        setLoading(false);
        return;
      }
      
      // No session_id in hash, check existing auth
      await checkAuth();
      setLoading(false);
    };
    
    init();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Ignore error
    }
    sessionStorage.removeItem('session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
